import { Money, Dimensions, Address, formatSize, isDealFree, isDealPrepaid, getDealDiscountType, isDealChargeAmount, getEffectiveDealRate, formatMoney, formatPriceLabel, getNumberWithOrdinalSuffix } from './chunk-SJ2EGAN4.js';
import { __export } from './chunk-PZ5AY32C.js';
import currencyJs from 'currency.js';

// src/domain/index.ts
var domain_exports = {};
__export(domain_exports, {
  Address: () => Address,
  AutoBillType: () => AutoBillType,
  Collection: () => Collection,
  Deal: () => Deal,
  DealCollection: () => DealCollection,
  Descriptor: () => Descriptor,
  DescriptorCollection: () => DescriptorCollection,
  Dimensions: () => Dimensions,
  DomainObject: () => DomainObject,
  Money: () => Money,
  Reservation: () => Reservation,
  Settings: () => Settings,
  Site: () => Site,
  SiteDimensions: () => SiteDimensions,
  UnitType: () => UnitType,
  UnitTypeCollection: () => UnitTypeCollection,
  corpCode: () => corpCode,
  locationCode: () => locationCode,
  reservationToken: () => reservationToken,
  resolveTagIcon: () => resolveTagIcon,
  toFriendlyDescriptor: () => toFriendlyDescriptor,
  unitTypeId: () => unitTypeId
});

// src/domain/values/SiteDimensions.ts
var SiteDimensions = class extends Dimensions {
  /**
   * @param length - Length (depth) in `unit`.
   * @param width - Width (frontage) in `unit`.
   * @param unit - Unit of measure.
   * @param display - The site's {@link DimensionsDisplayContext}.
   * @param height - Optional height in `unit`.
   */
  constructor(length, width, unit, display, height) {
    super(length, width, unit, height);
    this.display = display;
  }
  /**
   * Formats the footprint — or the site's reported **area**, when it is configured to show area
   * instead of dimensions and these dimensions are not supplementary to a title that already
   * carries it.
   *
   * @param options - {@link SiteDimensionsFormatOptions}.
   * @returns e.g. `"5m × 3m (15m²)"`, or `"15m²"` on an area-preferring site.
   */
  format(options = {}) {
    if (!this.showsArea(options)) {
      const order = this.display.unitDimensionDisplay === "lengthByWidth" ? "lengthFirst" : "widthFirst";
      return super.format({ order, ...options });
    }
    return formatSize(this.display.reportedArea, {
      areaSizeSymbol: this.display.areaSizeSymbol,
      sizeSymbol: this.unit,
      showSymbol: options.showUnit ?? true
    });
  }
  /**
   * Whether this should render the site's area rather than the footprint.
   *
   * @param options - The caller's {@link SiteDimensionsFormatOptions}.
   * @returns `true` when the area is the figure to show.
   */
  showsArea(options) {
    if (this.display.unitDimensionDisplay !== "area") return false;
    const supplementary = options.supplementary ?? !this.display.useDescriptorTitle;
    return !supplementary;
  }
};

// src/domain/values/codes.ts
function nonEmpty(value, label) {
  if (typeof value !== "string" || value.trim() === "") {
    throw new TypeError(`${label} must be a non-empty string`);
  }
  return value;
}
function corpCode(value) {
  return nonEmpty(value, "CorpCode");
}
function locationCode(value) {
  return nonEmpty(value, "LocationCode");
}
function reservationToken(value) {
  return nonEmpty(value, "ReservationToken");
}
function unitTypeId(value) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new TypeError(`UnitTypeId must be a positive integer, got ${value}`);
  }
  return value;
}

// src/domain/base.ts
var SYMBOL_POSITIONS = ["none", "before", "after"];
function currencySymbolPositionOf(location) {
  const value = location.rapidstor.currencySymbolPositionOverride;
  return value !== void 0 && SYMBOL_POSITIONS.includes(value) ? value : "none";
}
function interpolate(template, params) {
  return template.replace(
    /\{\{(\w+)\}\}/g,
    (token, name) => name in params ? String(params[name]) : token
  );
}
var DomainObject = class {
  /**
   * @param context - The {@link DomainContext} to bind.
   */
  constructor(context) {
    this.context = context;
  }
  /** The bound location (site). */
  get location() {
    return this.context.location;
  }
  /**
   * The location's currency code, as configured. Returned verbatim — {@link Money} is where a code
   * is validated and upper-cased, so reading this never throws on a misconfigured site.
   */
  get currency() {
    return this.location.rapidstor.currency;
  }
  /** The location's configured billing period label, e.g. `'week'` when weekly rates are shown. */
  get billingPeriod() {
    return this.location.rapidstor.showWeeklyRates ? "week" : "month";
  }
  /**
   * Builds a {@link Money} in the location's currency, so callers never pass a currency code.
   *
   * @param major - The amount in major units (e.g. dollars).
   * @returns A `Money` tagged with the location's currency.
   * @example
   * this.money(249).format(); // "$249.00"
   */
  money(major) {
    return Money.of(major, this.currency);
  }
  /**
   * The site's own currency-display conventions, as options for `utils/currency`.
   *
   * Defined once here because `Site.formatRate`, `UnitType.formatRate`/`formatBaseRate` and
   * `Deal.priceDisplay` all render prices for the same site and must agree on its symbol, that
   * symbol's *placement*, and its billing period. They each rebuilt this object before, which is
   * how a setting could reach one price on a page and not another.
   *
   * @returns The site's defaults, for a caller to spread its own overrides over.
   */
  priceLabelDefaults() {
    const { rapidstor, settings } = this.location;
    return {
      currencySymbol: rapidstor.currencySymbol,
      currencySymbolPosition: currencySymbolPositionOf(this.location),
      currency: rapidstor.currency,
      weekly: this.billingPeriod === "week",
      billingDefault: settings.billingDefault
    };
  }
  /**
   * Resolves a user-visible string through the context's {@link DomainTranslator}, falling back to
   * the SDK's own wording when no resolver is bound.
   *
   * `{{token}}` placeholders are interpolated from `params` afterwards, whichever branch supplied
   * the string — so a resolver is free to return a template and need not interpolate itself.
   *
   * @param key - The string's stable key (see `docs/domain.md` for the full set).
   * @param fallback - The SDK's own English wording.
   * @param params - Values for any `{{token}}` placeholders.
   * @returns The resolved, interpolated string.
   * @example
   * this.text('deal.monthFree', 'Month {{months}} free', { months: 3 }); // "Month 3 free"
   */
  text(key, fallback, params) {
    const resolved = this.context.translate?.(key, fallback, params) ?? fallback;
    return params ? interpolate(resolved, params) : resolved;
  }
};

// ../rapidstor-types/dist/enums/sitelink.mjs
var AutoBillType = /* @__PURE__ */ ((AutoBillType2) => {
  AutoBillType2[AutoBillType2["None"] = 0] = "None";
  AutoBillType2[AutoBillType2["Unknown"] = -1] = "Unknown";
  AutoBillType2[AutoBillType2["CreditCard"] = 1] = "CreditCard";
  AutoBillType2[AutoBillType2["DirectDebit"] = 2] = "DirectDebit";
  return AutoBillType2;
})(AutoBillType || {});

// src/domain/Settings.ts
var DEFAULT_SORT_ORDER = ["size", "price", "availability"];
var Settings = class extends DomainObject {
  /**
   * Autobill (recurring payment) configuration.
   *
   * @returns The {@link AutobillSettings} accessors.
   */
  autobill() {
    const { rapidstor } = this.location;
    const takingDirectCard = !rapidstor.paymentGateways.use;
    return {
      show: () => rapidstor.useAutoBilling && takingDirectCard,
      required: () => rapidstor.requireAutobill,
      allowDirectDebit: () => rapidstor.directDebit.enabled,
      allowCreditCard: () => rapidstor.creditCardForAutobill.enabled,
      allowSwitchingToDebit: () => rapidstor.tenantAccounts.autoPayment.enableSwitchingToDebit,
      forceInitialCard: () => rapidstor.forceInitialCard,
      defaultType: (options = {}) => {
        const { simplified = false } = options;
        const checkedOrRequired = rapidstor.autoBillCheckedByDefault || rapidstor.requireAutobill;
        if (!takingDirectCard || !checkedOrRequired) {
          return AutoBillType.None;
        }
        if (!simplified && rapidstor.directDebit.enabled && rapidstor.autoBillCheckedByDefault) {
          return AutoBillType.DirectDebit;
        }
        return rapidstor.creditCardForAutobill.enabled ? AutoBillType.CreditCard : AutoBillType.None;
      }
    };
  }
  /**
   * Insurance configuration.
   *
   * @returns The {@link InsuranceSettings} accessors.
   */
  insurance() {
    const moveInSingle = this.location.rapidstor.wizards.moveinSingle;
    return {
      hidden: () => moveInSingle.hideInsurance,
      required: () => moveInSingle.insuranceRequired,
      optional: () => !moveInSingle.insuranceRequired
    };
  }
  /**
   * Which journey actions this site permits.
   *
   * @returns The {@link JourneySettings} accessors.
   */
  journey() {
    const { rapidstor } = this.location;
    return {
      allowMoveIn: () => rapidstor.allowMoveIn,
      allowReservation: () => rapidstor.allowReservation,
      allowSaveQuote: () => rapidstor.allowSaveQuote,
      useDealCoupons: () => rapidstor.useDealCoupons
    };
  }
  /**
   * Price and unit-size presentation preferences.
   *
   * @returns The {@link DisplaySettings} accessors.
   */
  display() {
    const { rapidstor, settings } = this.location;
    return {
      showWeeklyRates: () => rapidstor.showWeeklyRates,
      hidePrices: () => rapidstor.hidePrices,
      useDescriptorTitle: () => rapidstor.useDescriptorTitle,
      unitDimensionDisplay: () => settings.unitDimensionDisplay,
      sizeSymbol: () => rapidstor.sizeSymbol,
      areaSizeSymbol: () => rapidstor.areaSizeSymbol,
      currencySymbol: () => rapidstor.currencySymbol,
      currencySymbolPosition: () => currencySymbolPositionOf(this.location)
    };
  }
  /**
   * How this site merchandises its unit listing.
   *
   * These settings describe the listing itself — how unit types are grouped, ordered, bracketed
   * and filtered — which is why they have no home in {@link Settings.display}, whose concern is
   * how an individual price or size is *rendered*.
   *
   * @returns The {@link ListingSettings} accessors.
   * @example
   * const listing = site.settings.listing();
   * if (listing.useMultilevel()) units = units.bestPerFloorGroup();
   * units = units.sortBy(listing.sortOrder());
   */
  listing() {
    const { rapidstor, settings, skin } = this.location;
    return {
      useMultilevel: () => rapidstor.useMultilevel ?? false,
      groupDescriptors: () => rapidstor.groupDescriptors ?? false,
      showQuickSearch: () => !rapidstor.hideQuickSearch,
      // Only an explicit `false` opts out: the vehicle filter predates the setting, so a
      // payload that omits it is a site that never turned it off.
      showVehicleQuickSearch: () => rapidstor.showVehicleQuickSearch !== false,
      sortOrder: () => {
        const configured = settings.unitSortOrder ?? [];
        const criteria = configured.filter(
          (criterion) => criterion !== "none"
        );
        return criteria.length > 0 ? criteria : [...DEFAULT_SORT_ORDER];
      },
      // `locationTransformer` already normalises this to a number, so no coercion is needed —
      // but default it anyway: a payload missing it would otherwise make every unit type's
      // floor category read as "below ground", silently emptying a multilevel listing.
      groundFloor: () => settings.groundFloor ?? 0,
      smallMediumMaxArea: () => rapidstor.smallMediumSizes,
      mediumLargeMaxArea: () => rapidstor.mediumLargeSizes,
      minAvailableUnits: () => rapidstor.minAvailableUnits,
      hideBelowMinAvailable: () => rapidstor.hideUnitsMinAvailable ?? false,
      variant: () => rapidstor.features?.["unit-listing"],
      defaultUnitPicture: () => skin.defaultUnitPicture
    };
  }
  /**
   * The operator-configurable copy a storefront renders.
   *
   * Unlike the rest of `Settings`, these return **text a human reads**, so each one routes through
   * the bound {@link DomainTranslator} — that is the seam a corporation-level copy override hangs
   * off, matching the precedence the legacy storefront implemented by hand.
   *
   * @returns The {@link LabelSettings} accessors.
   * @example
   * site.settings.labels().saveQuoteButton(); // the site's wording, or a corp override
   */
  labels() {
    const { rapidstor } = this.location;
    return {
      saveQuoteButton: () => this.setting("settings.saveQuoteButtonText", rapidstor.saveQuoteButtonText, "Save quote"),
      showPricesButton: () => this.setting("settings.showPricesButtonText", rapidstor.showPricesButtonText, "Show prices"),
      noAvailableUnits: () => this.setting("settings.noAvailableUnitsText", rapidstor.noAvailableUnitsText, "Call us now"),
      payNowButton: () => this.setting("settings.payNowButtonText", rapidstor.payNowButtonText, "Pay now"),
      payLaterButton: () => this.setting("settings.payLaterButtonText", rapidstor.payLaterButtonText, "Pay later")
    };
  }
  /**
   * Resolves one piece of settings-sourced copy: the operator's own wording when they configured
   * any, else the SDK's English default, with a bound translator able to override either.
   *
   * A blank setting is treated as unconfigured rather than as empty copy — an operator who cleared
   * a field wants the default label, not a button with no text on it.
   *
   * @param key - The translation key (see `docs/domain.md`).
   * @param configured - The site's configured wording, if any.
   * @param fallback - The SDK's English default.
   * @returns The wording to display.
   */
  setting(key, configured, fallback) {
    return this.text(key, configured?.trim() || fallback);
  }
};

// ../rapidstor-types/dist/react/entities/Location.mjs
var paymentGatewayTransformer = (location) => {
  const result = {
    use: location.rapidstor.paymentGateways.use,
    gatewayToUse: location.rapidstor.paymentGateways.gatewayToUse,
    bypassSitelinkProcessing: location.rapidstor.paymentGateways.bypassSitelinkProcessing,
    gateways: {}
  };
  if (location.rapidstor.paymentGateways.use) {
    switch (location.rapidstor.paymentGateways.gatewayToUse) {
      case "r6Pay":
        result.gateways = {
          r6Pay: {
            currency: location.rapidstor.paymentGateways.r6Pay.currency,
            useTestEnv: location.rapidstor.paymentGateways.r6Pay.useTestEnv,
            accessToken: location.rapidstor.paymentGateways.r6Pay.accessToken
          }
        };
        break;
    }
  }
  return result;
};
var locationTransformer = (location) => {
  const name = location.sSiteName.toLowerCase().trim().replace(/[\s_'-]+/g, "-").replace(/[^\w\s-]/g, "").replace(/^-+|-+$/g, "");
  return {
    name: location.sSiteName,
    slug: name,
    locationId: location.SiteID,
    locationCode: location.sLocationCode,
    corpCode: location.sCorpCode,
    contactName: location.sContactName,
    contactEmail: location.sEmailAddress,
    contactPhone: location.sSitePhone,
    websiteUrl: location.sWebSiteURL,
    latitude: parseFloat(location.dcLatitude),
    longitude: parseFloat(location.dcLongitude),
    timezoneOffset: location.iGMTTimeOffset,
    administrative: {
      testMode: location.administrative.testMode,
      useMtasForAuth: location.administrative.useMtasForAuth
    },
    address: {
      address1: location.sSiteAddr1,
      address2: location.sSiteAddr2,
      city: location.sSiteCity,
      region: location.sSiteRegion,
      postalCode: location.sSitePostalCode,
      country: location.sSiteCountry
    },
    rapidstor: {
      allowMoveIn: location.rapidstor.allowMoveIn,
      allowReservation: location.rapidstor.allowReservation,
      allowSaveQuote: location.rapidstor.allowSaveQuote,
      allowedCreditCardTypes: {
        visa: location.rapidstor.allowedCreditCardTypes.visa,
        mastercard: location.rapidstor.allowedCreditCardTypes.mastercard,
        amex: location.rapidstor.allowedCreditCardTypes.amex,
        discover: location.rapidstor.allowedCreditCardTypes.discover
      },
      areaSizeSymbol: location.rapidstor.areaSizeSymbol,
      completeMoveinUrl: location.rapidstor.completeMoveinURL,
      reservationNumberOfDaysInAdvanceLimit: location.rapidstor.reservationNumberOfDaysInAdvanceLimit,
      reservationNumberOfDaysInAdvanceMinimum: location.rapidstor.reservationNumberOfDaysInAdvanceMinimum,
      reservationNumberOfDaysInAdvanceChargeFee: location.rapidstor.reservationNumberOfDaysInAdvanceChargeFee,
      moveinNumberOfDaysInAdvanceLimit: location.rapidstor.moveinNumberOfDaysInAdvanceLimit,
      includeReservedUnitsInOccupied: location.rapidstor.includeReservedUnitsInOccupied,
      country: location.rapidstor.country,
      currency: location.rapidstor.currency,
      currencySymbol: location.rapidstor.currencySymbol,
      currencySymbolPositionOverride: location.rapidstor.currencySymbolPositionOverride ?? "none",
      autoESignRedirect: location.rapidstor.autoESignRedirect,
      eSignReturnUrl: location.rapidstor.eSignReturnUrl,
      jblRedirects: {
        afterMoveIn: {
          enabled: location.rapidstor.jblRedirects?.afterMoveIn?.enabled ?? false,
          url: location.rapidstor.jblRedirects?.afterMoveIn?.url ?? ""
        },
        afterUnpaidReservation: {
          enabled: location.rapidstor.jblRedirects?.afterUnpaidReservation?.enabled ?? false,
          url: location.rapidstor.jblRedirects?.afterUnpaidReservation?.url ?? ""
        },
        afterPaidReservation: {
          enabled: location.rapidstor.jblRedirects?.afterPaidReservation?.enabled ?? false,
          url: location.rapidstor.jblRedirects?.afterPaidReservation?.url ?? ""
        }
      },
      dateFormat: location.rapidstor.dateFormat,
      mediumLargeSizes: location.rapidstor.mediumLargeSize,
      smallMediumSizes: location.rapidstor.smallMediumSize,
      pushRate: location.rapidstor.pushRate,
      showWeeklyRates: location.rapidstor.showWeeklyRates,
      siteLogo: location.rapidstor.siteLogo,
      sizeSymbol: location.rapidstor.sizeSymbol,
      hideUnitsMinAvailable: location.rapidstor.hideUnitsMinAvailable,
      hideQuickSearch: location.rapidstor.hideQuickSearch,
      showVehicleQuickSearch: location.rapidstor.showVehicleQuickSearch,
      spaceCalculatorValues: location.rapidstor.spaceCalculatorValues,
      groupDescriptors: location.rapidstor.unitListing.groupDescriptors,
      useMultilevel: location.rapidstor.unitListing.useMultilevel,
      minAvailableUnits: location.rapidstor.minAvailableUnits,
      useDescriptorTitle: location.rapidstor.useDescriptorTitle,
      showPricesButtonText: location.rapidstor.showPricesButtonText,
      quickReserveButtonText: location.rapidstor.quickReserveButtonText,
      payNowButtonText: location.rapidstor.payNowButtonText,
      payLaterButtonText: location.rapidstor.payLaterButtonText,
      showPricesShowNeeded: location.rapidstor.showPricesShowNeeded,
      showPricesCreatesLead: location.rapidstor.showPricesCreatesLead,
      showPricesBehaviour: {
        createLead: location.rapidstor.showPricesBehaviour.createLead,
        sendEmail: location.rapidstor.showPricesBehaviour.sendEmail
      },
      tenantAccounts: location.rapidstor.tenantAccounts,
      hidePrices: location.rapidstor.hidePrices,
      hideStandardPrices: location.rapidstor.hideStandardPrices,
      noAvailableUnitsText: location.rapidstor.noAvailableUnitsText,
      noAvailableUnitsSecondaryText: location.rapidstor.noAvailableUnitsSecondaryText,
      noAvailableUnitsSecondaryAction: location.rapidstor.noAvailableUnitsSecondaryAction,
      callSiteCta: {
        label: location.rapidstor.callSiteCta?.label ?? "",
        type: location.rapidstor.callSiteCta?.type ?? "phone",
        value: location.rapidstor.callSiteCta?.value ?? ""
      },
      saveQuoteButtonText: location.rapidstor.saveQuoteButtonText,
      saveQuotePopupText: location.rapidstor.saveQuotePopupText,
      useDealCoupons: location.rapidstor.useDealCoupons,
      useBoxShopCoupons: location.rapidstor.useBoxShopCoupons,
      dangerousGoodsWarning: location.rapidstor.dangerousGoodsWarning,
      assistanceMoving: location.rapidstor.assistanceMoving,
      assistanceMovingEmailAddress: location.rapidstor.assistanceMovingEmailAddress,
      pickupEnabled: location.rapidstor.pickupEnabled,
      legacyPickupLocationsEnabled: location.rapidstor.legacyPickupLocationsEnabled,
      legacyPickupLocations: location.rapidstor.legacyPickupLocations,
      wizards: {
        moveinSingle: location.rapidstor.wizards.moveinSingle,
        common: {
          titles: location.rapidstor.wizards.common?.titles
        }
      },
      creditCardContent: location.rapidstor.wizards.moveinSingle?.creditCardContent,
      directDebitContent: location.rapidstor.wizards.moveinSingle?.directDebitContent,
      paymentGateways: paymentGatewayTransformer(location),
      useAutoBilling: location.rapidstor.useAutoBilling,
      autoBillCheckedByDefault: location.rapidstor.autoBillCheckedByDefault,
      requireAutobill: location.rapidstor.requireAutobill,
      creditCardForAutobill: {
        enabled: location.rapidstor.creditCardForAutobill.enabled
      },
      directDebit: {
        enabled: location.rapidstor.directDebit.enabled,
        creditAmount: location.rapidstor.directDebit.creditAmount
      },
      datesClosed: location.rapidstor.datesClosed,
      siteOpeningDate: location.rapidstor.siteOpeningDate,
      carouselListing: location.administrative.carouselListing,
      moveinRedirectUrl: location.rapidstor.moveinRedirectUrl,
      forceInitialCard: location.rapidstor.forceInitialCard,
      alwaysAllowPricingPage: location.rapidstor.alwaysAllowPricingPage,
      privacyPolicyUrl: location.rapidstor.privacyPolicyUrl ?? "",
      termsAndConditionsUrl: location.rapidstor.termsAndConditionsUrl ?? "",
      features: location.rapidstor.features
    },
    scaConfiguration: location.scaConfiguration,
    deleted: location.deleted,
    archived: location.bArchived == "true",
    rapidstorEnabled: location.rapidstorEnabled,
    token: location.token,
    createdAt: location.dCreated,
    updatedAt: location.dUpdated,
    settings: {
      logo: location.rapidstor.siteLogo,
      groundFloor: Number(location.rapidstor.groundFloor || 0),
      groupUnitsByFloor: location.rapidstor.unitListing.useMultilevel,
      billingDefault: location.rapidstor.billingDefault,
      sizeSymbol: location.rapidstor.sizeSymbol,
      areaSizeSymbol: location.rapidstor.areaSizeSymbol,
      allowMoveIn: location.rapidstor.allowMoveIn,
      unitDimensionDisplay: location.rapidstor.unitDimensionDisplay ?? "widthByLength",
      marketingSurvey: location.rapidstor.marketingSurvey,
      unitSortOrder: [
        location.rapidstor.unitListing.sortOrder.primary,
        location.rapidstor.unitListing.sortOrder.secondary,
        location.rapidstor.unitListing.sortOrder.tertiary
      ].filter((sortOrder) => sortOrder !== "none")
    },
    skin: {
      accentColours: {
        primary: location.skin.accentColours.primary
      },
      defaultUnitPicture: location.skin.defaultUnitPicture,
      mainColour: location.skin.mainColour,
      highlightColour: location.skin.highlightColour,
      themeColour: location.skin.themeColour,
      moveinTimeRemaining: location.skin.moveinTimeRemaining
    },
    hours: location.hours
  };
};

// src/domain/collections/Collection.ts
var Collection = class {
  /**
   * @param items - The items to hold. The array is not copied; treat it as owned by the collection.
   */
  constructor(items) {
    this.items = items;
  }
  /** How many items the collection holds. */
  get length() {
    return this.items.length;
  }
  /** Whether the collection holds no items. */
  get isEmpty() {
    return this.items.length === 0;
  }
  /** Iterates the items, so a collection works with `for…of` and spread. */
  [Symbol.iterator]() {
    return this.items[Symbol.iterator]();
  }
  /**
   * The items as a plain array.
   *
   * @returns A shallow copy, safe for the caller to mutate.
   */
  toArray() {
    return [...this.items];
  }
  /**
   * The item at a position.
   *
   * @param index - Zero-based index; negative counts from the end.
   * @returns The item, or `undefined` if out of range.
   */
  at(index) {
    return index < 0 ? this.items[this.items.length + index] : this.items[index];
  }
  /** The first item, or `undefined` when empty. */
  first() {
    return this.items[0];
  }
  /** The last item, or `undefined` when empty. */
  last() {
    return this.items[this.items.length - 1];
  }
  /**
   * Keeps only the items matching a predicate.
   *
   * @param predicate - Returns `true` to keep the item.
   * @returns A new collection of the same subclass.
   */
  filter(predicate) {
    return this.create(this.items.filter((item, index) => predicate(item, index)));
  }
  /**
   * Orders the items.
   *
   * @param comparator - Ordering function, as `Array.prototype.sort` takes.
   * @returns A new, sorted collection of the same subclass (the original is untouched).
   */
  sort(comparator) {
    return this.create([...this.items].sort(comparator));
  }
  /**
   * Finds the first item matching a predicate.
   *
   * @param predicate - Returns `true` for the wanted item.
   * @returns The item, or `undefined` if none match.
   */
  find(predicate) {
    return this.items.find((item, index) => predicate(item, index));
  }
  /**
   * Projects every item through a function.
   *
   * @param fn - The projection.
   * @returns A plain array of the projected values.
   */
  map(fn) {
    return this.items.map((item, index) => fn(item, index));
  }
  /**
   * Whether any item matches a predicate.
   *
   * @param predicate - The test.
   * @returns `true` if at least one item matches.
   */
  some(predicate) {
    return this.items.some((item, index) => predicate(item, index));
  }
  /**
   * Whether every item matches a predicate.
   *
   * @param predicate - The test.
   * @returns `true` if all items match (vacuously `true` when empty).
   */
  every(predicate) {
    return this.items.every((item, index) => predicate(item, index));
  }
  /**
   * Partitions the items by a derived key.
   *
   * Each group is a **new collection of this same subclass**, not a plain array, so the domain
   * queries stay available on the way out — `units.groupBy(...)` yields groups you can go on to
   * `sortBy('price')`.
   *
   * @param key - Derives the group key for an item.
   * @returns A map of key to collection, in first-seen key order.
   * @example
   * const byDescriptor = units.groupBy((unitType) => unitType.descriptor?.id);
   * byDescriptor.get('desc_1')?.sortBy('price').first();
   */
  groupBy(key) {
    const buckets = /* @__PURE__ */ new Map();
    this.items.forEach((item, index) => {
      const bucketKey = key(item, index);
      const bucket = buckets.get(bucketKey);
      if (bucket) bucket.push(item);
      else buckets.set(bucketKey, [item]);
    });
    return new Map([...buckets].map(([bucketKey, items]) => [bucketKey, this.create(items)]));
  }
  /**
   * Builds a new collection of this same concrete subclass. Subclasses whose constructor takes
   * more than the item array must override this so chained operations preserve their extra state.
   *
   * @param items - The items for the new collection.
   * @returns A new collection of the runtime subclass.
   */
  create(items) {
    const Ctor = this.constructor;
    return new Ctor(items);
  }
};

// src/domain/collections/DescriptorCollection.ts
var DescriptorCollection = class extends Collection {
  /**
   * Finds a descriptor by its `_id`.
   *
   * @param id - The descriptor's `_id`.
   * @returns The descriptor, or `undefined` if this collection holds no such id.
   * @example
   * const descriptor = descriptors.get('64f0c2a1e4b0a1');
   */
  get(id) {
    return this.find((descriptor) => descriptor.id === id);
  }
  /**
   * Filters by declarative criteria — the options-object counterpart to
   * {@link Collection.filter}'s predicate. Omitted criteria are simply not applied.
   *
   * @param criteria - The {@link DescriptorCriteria} to apply.
   * @returns A new collection of the matching descriptors.
   */
  where(criteria) {
    return this.filter((descriptor) => {
      if (criteria.available !== void 0 && descriptor.isAvailable !== criteria.available) {
        return false;
      }
      if (criteria.enabled !== void 0 && descriptor.enabled !== criteria.enabled) {
        return false;
      }
      if (criteria.hidden !== void 0 && descriptor.hidden !== criteria.hidden) {
        return false;
      }
      if (criteria.maxPrice !== void 0 || criteria.minPrice !== void 0) {
        const from = descriptor.priceRange()?.from;
        if (!from) return false;
        if (criteria.maxPrice !== void 0 && from.greaterThan(criteria.maxPrice)) {
          return false;
        }
        if (criteria.minPrice !== void 0 && from.lessThan(criteria.minPrice)) {
          return false;
        }
      }
      return true;
    });
  }
  /**
   * Keeps only descriptors with at least one available unit type.
   *
   * @returns A new collection of the available descriptors.
   */
  available() {
    return this.where({ available: true });
  }
  /**
   * Keeps only descriptors that are enabled and not hidden — the set a storefront should show.
   *
   * @returns A new collection of the displayable descriptors.
   */
  displayable() {
    return this.where({ enabled: true, hidden: false });
  }
  /**
   * Orders the collection by a named key. `ordinal` uses the operator-configured display order;
   * `price` uses each descriptor's cheapest rate, with descriptors that have nothing to price
   * sorted last rather than treated as free.
   *
   * @param key - The {@link DescriptorSortKey} to order by.
   * @returns A new, ordered collection.
   */
  sortBy(key) {
    switch (key) {
      case "ordinal":
        return this.sort((a, b) => a.ordinalPosition - b.ordinalPosition);
      case "price":
        return this.sort((a, b) => {
          const left = a.priceRange()?.from;
          const right = b.priceRange()?.from;
          if (!left) return right ? 1 : 0;
          return right ? left.compare(right) : -1;
        });
      case "size":
        return this.sort((a, b) => a.area - b.area);
      case "name":
        return this.sort((a, b) => a.name.localeCompare(b.name));
    }
  }
  /**
   * Every descriptor's display title.
   *
   * @returns The titles, in collection order.
   */
  titles() {
    return this.map((descriptor) => descriptor.title());
  }
  /**
   * Every descriptor's listing-card summary.
   *
   * @returns The {@link DescriptorSummary} objects, in collection order.
   * @example
   * descriptors.available().summaries(); // [{ title, priceFrom, sizeLabel, thumbnail, tags }, ...]
   */
  summaries() {
    return this.map((descriptor) => descriptor.summary());
  }
  /**
   * Every descriptor's rate span.
   *
   * @returns The {@link DescriptorPriceRange} objects, in collection order. A descriptor with no
   *   unit types to price contributes `undefined`, so the result stays index-aligned with the
   *   collection.
   */
  priceRanges() {
    return this.map((descriptor) => descriptor.priceRange());
  }
  /**
   * The cheapest rate offered across every descriptor in the collection.
   *
   * @returns The lowest rate as {@link Money}, or `undefined` when the collection is empty or
   *   nothing in it has a rate.
   */
  cheapestRate() {
    return this.sortBy("price").first()?.priceRange()?.from;
  }
};

// src/domain/collections/UnitTypeCollection.ts
function compareOn(key, a, b) {
  switch (key) {
    case "price":
      return a.payableRate.compare(b.payableRate);
    case "size":
      return a.area - b.area;
    case "floor":
      return a.floor - b.floor;
    case "availability":
      return b.totalAvailable - a.totalAvailable;
  }
}
var FLOOR_GROUP_ORDER = ["ground", "upper", "driveup"];
function displaces(candidate, incumbent) {
  if (candidate.isAvailable !== incumbent.isAvailable) return candidate.isAvailable;
  return candidate.payableRate.lessThan(incumbent.payableRate);
}
function inRange(value, range) {
  if (!range) return true;
  if (range.min !== void 0 && value < range.min) return false;
  if (range.max !== void 0 && value > range.max) return false;
  return true;
}
var UnitTypeCollection = class extends Collection {
  /**
   * Finds a unit type by its unique document id.
   *
   * @param id - The unit type's `id` (its document `_id`).
   * @returns The unit type, or `undefined` if this collection holds no such id.
   * @example
   * units.get('64f0c2a1e4b0a1');
   */
  get(id) {
    return this.find((unitType) => unitType.id === id);
  }
  /**
   * Finds a unit type by its SiteLink unit-type id.
   *
   * That id is **not unique within a listing** — the same SiteLink type can appear under several
   * descriptors — so this returns the first match and is the wrong tool for identity. Use
   * {@link UnitTypeCollection.get} when you need a specific listing entry.
   *
   * @param id - The SiteLink unit-type id.
   * @returns The first unit type with that id, or `undefined` if none has it.
   */
  getByTypeId(id) {
    return this.find((unitType) => unitType.unitTypeId === id);
  }
  /**
   * Filters by declarative criteria — the options-object counterpart to
   * {@link Collection.filter}'s predicate. Omitted criteria are simply not applied.
   *
   * @param criteria - The {@link UnitTypeCriteria} to apply.
   * @returns A new collection of the matching unit types.
   * @example
   * units.where({ available: true, inside: true, area: { min: 9 } });
   */
  where(criteria) {
    return this.filter((unitType) => {
      if (criteria.available !== void 0 && unitType.isAvailable !== criteria.available) {
        return false;
      }
      if (criteria.minAvailable !== void 0 && unitType.totalAvailable < criteria.minAvailable) {
        return false;
      }
      if (criteria.floor !== void 0 && unitType.floor !== criteria.floor) return false;
      if (criteria.floorGroup !== void 0 && unitType.floorGroup !== criteria.floorGroup) {
        return false;
      }
      if (criteria.inside !== void 0 && unitType.inside !== criteria.inside) return false;
      if (criteria.hasDeal !== void 0 && unitType.hasDeal !== criteria.hasDeal) {
        return false;
      }
      if (criteria.sizeCategory !== void 0 && unitType.sizeCategory !== criteria.sizeCategory) {
        return false;
      }
      if (criteria.vehicleSpace !== void 0 && unitType.isVehicleSpace !== criteria.vehicleSpace) {
        return false;
      }
      const price = unitType.payableRate;
      if (criteria.maxPrice !== void 0 && price.greaterThan(criteria.maxPrice)) {
        return false;
      }
      if (criteria.minPrice !== void 0 && price.lessThan(criteria.minPrice)) {
        return false;
      }
      const area = criteria.area?.basis === "footprint" ? unitType.dimensions.area : unitType.area;
      return inRange(area, criteria.area);
    });
  }
  /**
   * Keeps only unit types with at least one available unit.
   *
   * @returns A new collection of the available unit types.
   */
  available() {
    return this.where({ available: true });
  }
  /**
   * Keeps only unit types carrying a deal.
   *
   * @returns A new collection of the discounted unit types.
   */
  withDeals() {
    return this.where({ hasDeal: true });
  }
  /**
   * Keeps only unit types on a given floor.
   *
   * @param floor - The floor number (`0` is ground).
   * @returns A new collection of that floor's unit types.
   */
  onFloor(floor) {
    return this.where({ floor });
  }
  /**
   * Orders the collection by one key, or by several with the first as the primary and the rest as
   * tie-breakers.
   *
   * Ascending for `price`, `size` and `floor`; descending for `availability` (most available
   * first). `'none'` is the platform's "no criterion" marker and is skipped, so a site's configured
   * `unitSortOrder` can be handed over unfiltered.
   *
   * `price` compares {@link UnitType.payableRate} — what the customer actually pays, with any
   * attached deal's own rule applied. Not `discountedRate`: a free-period or charge-amount deal
   * carries one that is not an ongoing rate, so ordering on it would rank those units at `0`.
   *
   * @param keys - One {@link UnitTypeSortKey}, or several in priority order.
   * @returns A new, ordered collection.
   * @example
   * units.sortBy('price').first();                          // the cheapest unit type
   * units.sortBy(['size', 'price', 'availability']);          // size first, price breaks ties
   * units.sortBy(site.settings.listing().sortOrder());        // whatever the operator configured
   */
  sortBy(keys) {
    const criteria = (typeof keys === "string" ? [keys] : keys).filter(
      (key) => key !== "none"
    );
    return this.sort((a, b) => {
      for (const key of criteria) {
        const result = compareOn(key, a, b);
        if (result !== 0) return result;
      }
      return 0;
    });
  }
  /**
   * Moves available unit types ahead of unavailable ones, leaving the order within each group
   * untouched.
   *
   * Chain it *after* `sortBy` so availability wins and the sort breaks ties inside each group:
   * `units.sortBy(order).availableFirst()`.
   *
   * Replaces the legacy `sortUnitTypes`, whose sequential-`Array.sort` implementation applied the
   * configured criteria in *reverse* priority and ended with an available-first pass — an accident
   * of implementation rather than a stated intent. This states the intent.
   *
   * @returns A new collection with the available unit types first.
   */
  availableFirst() {
    return this.sort((a, b) => {
      if (a.isAvailable === b.isAvailable) return 0;
      return a.isAvailable ? -1 : 1;
    });
  }
  /**
   * Groups the collection by the descriptor that merchandises each unit type.
   *
   * The usual shape of a listing: flatten a site's descriptors into one pool to sort and filter it,
   * then group it back to render a card per descriptor. Relies on
   * {@link UnitType.descriptor}, so unit types with no descriptor group under `undefined`.
   *
   * @returns A map of descriptor id to that descriptor's unit types, in first-seen order.
   * @example
   * for (const [, group] of units.groupByDescriptor()) renderCard(group);
   */
  groupByDescriptor() {
    return this.groupBy((unitType) => unitType.descriptor?.id);
  }
  /**
   * Reduces the collection to at most one unit type per floor category — the multilevel listing's
   * central operation.
   *
   * Within each of ground / upper / drive-up, an **available** unit type beats an unavailable one,
   * and the **cheaper** wins between two of equal availability. Unit types below the site's ground
   * floor have no category and are dropped. The result is ordered ground → upper → drive-up.
   *
   * "Cheaper" is the same figure {@link UnitTypeCollection.sortBy}'s `price` uses, so a card's
   * chosen variant and its price ordering can't disagree.
   *
   * @returns A new collection holding at most three unit types.
   * @example
   * const variants = descriptor.unitTypes.bestPerFloorGroup();
   */
  bestPerFloorGroup() {
    const best = /* @__PURE__ */ new Map();
    for (const unitType of this) {
      const group = unitType.floorGroup;
      if (!group) continue;
      const incumbent = best.get(group);
      if (!incumbent || displaces(unitType, incumbent)) best.set(group, unitType);
    }
    return this.create(
      FLOOR_GROUP_ORDER.map((group) => best.get(group)).filter(
        (unitType) => unitType !== void 0
      )
    );
  }
  /**
   * The cheapest unit type.
   *
   * @returns The lowest-rate unit type, or `undefined` when the collection is empty.
   */
  cheapest() {
    return this.sortBy("price").first();
  }
  /**
   * The rate span across the collection, as a customer would see it.
   *
   * Reports the same discount-aware figure {@link UnitTypeCollection.sortBy}'s `price` orders by —
   * a discounted unit contributes what it actually costs, not its list rate. Reading `.rate` off
   * the sorted endpoints instead would pick the right units and then quote the wrong prices.
   *
   * @returns `{ from, to }` as {@link Money}, or `undefined` when the collection is empty.
   */
  priceRange() {
    const sorted = this.sortBy("price");
    const cheapest = sorted.first();
    const dearest = sorted.last();
    return cheapest && dearest ? { from: cheapest.payableRate, to: dearest.payableRate } : void 0;
  }
  /** Every unit type's total available count, summed. */
  get totalAvailable() {
    return this.map((unitType) => unitType.totalAvailable).reduce((a, b) => a + b, 0);
  }
};

// src/domain/collections/DealCollection.ts
var DealCollection = class extends Collection {
  /**
   * Finds a deal by its id.
   *
   * Replaces `Utils.findMatchingDeal` for callers holding wrapped deals — no raw payload needed.
   *
   * @param id - The deal's id.
   * @returns The deal, or `undefined` if this collection holds no such id.
   * @example
   * unitType.deals.get(selectedDealId);
   */
  get(id) {
    return this.find((deal) => deal.id === id);
  }
  /**
   * The deal a storefront should pre-select — the first, which is the one the platform's
   * `unitTypeTransformer` promotes onto the unit type's own `rates`.
   *
   * @returns The promoted deal, or `undefined` when the unit type carries none.
   */
  promoted() {
    return this.first();
  }
  /**
   * Orders the collection by what the customer actually pays, cheapest first.
   *
   * @param standardRate - The unit's own rate, which each deal's discount is measured against.
   * @returns A new, ordered collection.
   * @throws {TypeError} If `standardRate` is a different currency to a deal in the collection.
   * @example
   * unitType.deals.sortByEffectiveRate(unitType.rate).first(); // the best offer
   */
  sortByEffectiveRate(standardRate) {
    return this.sort(
      (a, b) => a.effectiveRateAgainst(standardRate).compare(b.effectiveRateAgainst(standardRate))
    );
  }
};
function stripHtml(html) {
  const entities = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&nbsp;": " "
  };
  return html.replace(/<[^>]*>/g, "").replace(/&(?:amp|lt|gt|quot|#39|nbsp);/g, (entity) => entities[entity] ?? entity).trim();
}
var Deal = class extends DomainObject {
  /**
   * @param raw - The raw `Deal` this wraps.
   * @param context - The {@link DomainContext} to bind.
   */
  constructor(raw, context) {
    super(context);
    this.raw = raw;
  }
  /** The deal's identifier. */
  get id() {
    return this.raw._id;
  }
  /** The deal's display name (its `title`). */
  get name() {
    return this.raw.title;
  }
  /** The deal's descriptive bullet lines, joined into a single line. */
  get description() {
    return this.raw.descriptions.join(" ");
  }
  /** The deal's descriptive bullet lines, unjoined — e.g. for rendering a bullet list. */
  get descriptions() {
    return this.raw.descriptions;
  }
  /** Whether this deal makes the unit entirely free for its discount period. */
  get isFree() {
    return isDealFree(this.raw);
  }
  /** Whether this deal requires prepayment. */
  get isPrepaid() {
    return isDealPrepaid(this.raw);
  }
  /** How this deal's discount is expressed. */
  get discountType() {
    return getDealDiscountType(this.raw);
  }
  /**
   * Whether this deal uses a fixed one-off charge amount for a specific period — its
   * `discountedRate` is then a one-time special price, not an ongoing rate.
   */
  get isChargeAmount() {
    return isDealChargeAmount(this.raw);
  }
  /**
   * The rate to actually charge/display, in the site's currency — the standard rate for
   * free/charge-amount deals (see {@link Deal.isFree}, {@link Deal.isChargeAmount}), or the
   * deal's own discounted rate otherwise.
   */
  get effectiveRate() {
    return this.money(getEffectiveDealRate(this.raw, this.raw.rates.rate));
  }
  /** The deal's own undiscounted standard rate, in the site's currency. */
  get standardRate() {
    return this.money(this.raw.rates.rate);
  }
  /**
   * The amount saved versus {@link Deal.standardRate}, in the site's currency. A charge-amount
   * deal (see {@link Deal.isChargeAmount}) has no ongoing savings to report, so this is `0` for
   * those deals rather than a misleading one-time figure.
   */
  get savings() {
    if (this.isChargeAmount) return this.money(0);
    const amount = this.raw.rates.discountSaved ?? this.savedAgainstStandard();
    return this.money(Math.max(amount, 0));
  }
  /**
   * The difference between this deal's standard and effective rates, subtracted through
   * `currency.js`.
   *
   * Plain subtraction is not safe on money: `249 - 199.99` is `49.00999999999999` in floating point.
   * Downstream rounding happens to hide that today, so this is about not depending on it — a caller
   * that formats to more places, or compares the figure, would see the artefact.
   *
   * @returns The saving, in major units.
   */
  savedAgainstStandard() {
    return currencyJs(this.raw.rates.rate).subtract(this.effectiveRate.major).value;
  }
  /**
   * Formats {@link Deal.savings} as a localized currency string.
   *
   * @param options - {@link MoneyFormatOptions}.
   * @returns e.g. `"$50.00"`.
   * @example
   * deal.formatSavings(); // "$50.00"
   */
  formatSavings(options) {
    return this.savings.format(options);
  }
  /**
   * Builds a legacy-style price display combining the deal's effective and standard rates —
   * reproducing the storefront's own price-label formatting (see `utils/currency`) rather than
   * `Money.format`'s locale-driven output.
   *
   * @param options - {@link DealPriceDisplayOptions}.
   * @returns The {@link DealPriceDisplay}.
   * @example
   * deal.priceDisplay(); // { effective: "$199/mo", standard: "$249/mo", savings: "$50/mo" }
   */
  priceDisplay(options = {}) {
    const priceLabelOptions = this.priceLabelOptions(options);
    const effectiveMajor = getEffectiveDealRate(this.raw, this.raw.rates.rate);
    const hasSavings = !this.isChargeAmount && effectiveMajor < this.raw.rates.rate;
    const saved = currencyJs(this.raw.rates.rate).subtract(effectiveMajor).value;
    return {
      effective: formatPriceLabel(effectiveMajor, priceLabelOptions),
      standard: formatPriceLabel(this.raw.rates.rate, priceLabelOptions),
      savings: hasSavings ? formatMoney(saved, priceLabelOptions) : void 0
    };
  }
  /**
   * The rate to charge when this deal is applied to a **unit's** rate rather than its own.
   *
   * Where a deal states its own `discountedRate`, that figure wins and this agrees with
   * {@link Deal.effectiveRate}. The two diverge exactly where the discounted rate is not the
   * figure to show:
   *
   * - a **free-period** or **charge-amount** deal, whose `discountedRate` (`0`, or a one-time
   *   price) would mislead as an ongoing rate — the baseline is returned instead;
   * - a deal carrying **no** `discountedRate` at all.
   *
   * In both cases `effectiveRate` falls back to the *deal's* rate, and this falls back to the
   * *unit's* — which is the figure a price card is comparing against.
   *
   * @param standardRate - The unit's rate (see `UnitType.rate`).
   * @returns The rate to actually charge, in the site's currency.
   * @throws {TypeError} If `standardRate` is not in this site's currency.
   * @example
   * deal.effectiveRateAgainst(unitType.rate).format(); // "$199.00"
   */
  effectiveRateAgainst(standardRate) {
    if (standardRate.currency !== this.currency) {
      throw new TypeError(
        `Cannot apply a deal priced in ${this.currency} to a rate in ${standardRate.currency}`
      );
    }
    return this.money(getEffectiveDealRate(this.raw, standardRate.major));
  }
  /**
   * The default copy shown above this deal's price, when its type has any.
   *
   * Covers the two deal shapes carrying a promise the rate alone cannot express: a free period
   * (`"Month 3 free"`) and a one-off charge amount (`"Pay $99 in your 2nd month"`). Percentage and
   * fixed discounts say all they need to through the price itself, so they have no default copy —
   * fall back to {@link Deal.name} or {@link Deal.customHeading} for those.
   *
   * Resolved through the context's translator under the `deal.*` keys (see `docs/domain.md`).
   *
   * @param options - {@link DealPriceDisplayOptions}, for the embedded amount's formatting.
   * @returns The copy, or `undefined` when this deal type has no default.
   * @example
   * deal.label(); // "Pay $99 in your 2nd month for 3 months"
   */
  label(options = {}) {
    if (this.isFree) {
      const months2 = this.raw.discountPlan?.iExpirMonths;
      return months2 ? this.text("deal.monthFree", "Month {{months}} free", { months: months2 }) : void 0;
    }
    if (!this.isChargeAmount) return void 0;
    const { discountPlan, rates } = this.raw;
    const amount = formatPriceLabel(rates.discountedRate ?? rates.rate, {
      ...this.priceLabelOptions(options),
      shortForm: true
    });
    const month = getNumberWithOrdinalSuffix(discountPlan.iInMonth);
    const months = discountPlan.iExpirMonths ?? 0;
    return months > 1 ? this.text(
      "deal.chargeInMonthForMonths",
      "Pay {{amount}} in your {{month}} month for {{months}} months",
      { amount, month, months }
    ) : this.text("deal.chargeInMonth", "Pay {{amount}} in your {{month}} month", {
      amount,
      month
    });
  }
  /**
   * The operator's own authored heading for this deal, with its rate tokens substituted.
   *
   * Supports `{{DiscountRate}}`, `{{StandardRate}}`, `{{DiscountSaving}}`, `{{PrepaidAmount}}` and
   * `{{PrepaidSaving}}`. HTML is stripped, since the field is authored in a rich-text editor but
   * renders as a plain label. Unrecognised tokens are left in place rather than blanked, so a
   * typo in an operator's text is visible.
   *
   * @param options - {@link DealPriceDisplayOptions}, for the substituted amounts' formatting.
   * @returns The heading, or `undefined` when the operator authored none.
   * @example
   * deal.customHeading(); // "Was $249, now $199 — save $50!"
   */
  customHeading(options = {}) {
    const heading = this.raw.customFormat?.heading?.trim();
    if (!heading) return void 0;
    const { rates } = this.raw;
    const amount = (value) => formatPriceLabel(value, { ...this.priceLabelOptions(options), shortForm: true });
    const discounted = this.isFree ? 0 : rates.discountedRate ?? rates.rate;
    const saved = rates.discountSaved ?? 0;
    const tokens = {
      "{{DiscountRate}}": amount(discounted),
      "{{StandardRate}}": amount(rates.rate),
      "{{DiscountSaving}}": amount(saved),
      "{{PrepaidAmount}}": amount(rates.prepaidAmount ?? rates.rate),
      "{{PrepaidSaving}}": amount(saved)
    };
    return stripHtml(heading).replace(
      /\{\{(?:DiscountRate|StandardRate|DiscountSaving|PrepaidAmount|PrepaidSaving)\}\}/g,
      (token) => tokens[token] ?? token
    );
  }
  /**
   * The site's own currency-display conventions, as options for `utils/currency`.
   *
   * @param options - Caller overrides ({@link DealPriceDisplayOptions}).
   * @returns The merged {@link FormatPriceLabelOptions}.
   */
  priceLabelOptions(options = {}) {
    return {
      ...this.priceLabelDefaults(),
      locale: options.locale,
      longForm: options.longForm
    };
  }
};

// src/domain/UnitType.ts
var FLOOR_LABEL_KEYS = {
  ground: "floor.ground",
  upper: "floor.upper",
  driveup: "floor.driveUp"
};
var FLOOR_LABEL_FALLBACKS = {
  ground: "Ground",
  upper: "Upper",
  driveup: "Drive Up"
};
var UnitType = class extends DomainObject {
  /**
   * @param raw - The friendly `UnitType` entity this wraps.
   * @param context - The {@link DomainContext} to bind.
   * @param options - {@link UnitTypeOptions}.
   */
  constructor(raw, context, options = {}) {
    super(context);
    this.raw = raw;
    this.options = options;
  }
  /**
   * The site's listing settings.
   *
   * Lives here rather than on `DomainObject` because `Settings` extends `DomainObject`, so having
   * the base class reach for it would close a runtime import cycle.
   */
  get listingSettings() {
    return new Settings(this.context).listing();
  }
  /**
   * This unit type's unique identifier — the entity's document `_id`.
   *
   * Prefer this over {@link UnitType.unitTypeId} for identity: keying on the SiteLink id collapses
   * distinct listing entries, because the same unit type is reachable through more than one
   * descriptor.
   */
  get id() {
    return this.raw._id;
  }
  /**
   * The SiteLink unit-type identifier.
   *
   * **Not unique within a listing** — the same SiteLink type can appear under several descriptors,
   * so two different listing entries can share this id. Use {@link UnitType.id} to tell them apart.
   */
  get unitTypeId() {
    return unitTypeId(this.raw.unitTypeId);
  }
  /**
   * The descriptor that merchandises this unit type, or `undefined` when it has none — a unit type
   * adapted from a reservation's snapshot was never part of a listing.
   *
   * This is what lets a flattened pool of unit types still reach its merchandising (picture, tags,
   * description) without the caller carrying the association itself.
   */
  get descriptor() {
    return this.options.descriptor;
  }
  /**
   * The individual units of this type, as reported by the source.
   *
   * {@link UnitType.totalAvailable} is the canonical availability figure — it honours the site's
   * `includeReservedUnitsInOccupied` setting, which a raw row count does not.
   */
  get units() {
    return this.raw.units ?? [];
  }
  /** The unit type's display name, e.g. `"10x10"`. */
  get typeName() {
    return this.raw.typeName;
  }
  /** How many units of this type are currently available to book. */
  get totalAvailable() {
    return this.raw.totalAvailable;
  }
  /** The floor number this unit type is on (`0` for ground floor). */
  get floor() {
    return this.raw.floor;
  }
  /** Whether this unit type has indoor/climate-adjacent access. */
  get inside() {
    return this.raw.inside;
  }
  /**
   * Which floor category this unit type is merchandised under, relative to the site's configured
   * ground floor — or `null` for a floor **below** it, which listings exclude.
   *
   * The trichotomy is the multilevel listing's whole model: at ground level, indoor access is
   * "ground" and outdoor access is "drive up"; anything higher is "upper".
   *
   * Note `rapidstor-types` ships a `determineFloor` that hardcodes `floor > 0` instead of reading
   * `settings.groundFloor`, so it answers wrongly for any site whose ground level is not `0`. This
   * honours the setting.
   *
   * @example
   * descriptor.unitTypes.filter((unit) => unit.floorGroup === 'driveup');
   */
  get floorGroup() {
    const groundFloor = this.listingSettings.groundFloor();
    if (this.floor === groundFloor) return this.inside ? "ground" : "driveup";
    return this.floor > groundFloor ? "upper" : null;
  }
  /**
   * The display label for this unit type's floor, e.g. `"Ground"` / `"Upper"` / `"Drive Up"`.
   *
   * Resolved through the context's translator under the `floor.*` keys. A floor below the site's
   * ground level has no category and so renders its own number.
   */
  get floorLabel() {
    const group = this.floorGroup;
    if (!group) return String(this.floor);
    return this.text(FLOOR_LABEL_KEYS[group], FLOOR_LABEL_FALLBACKS[group]);
  }
  /**
   * The rate to charge — the unit's own standard rate, or the first attached deal's rate when
   * one is present (mirrors how the `rapidstor-types` `unitTypeTransformer` substitutes `rates`
   * for a deal).
   */
  get rate() {
    return this.money(this.raw.rates.rate);
  }
  /** The undiscounted standard (list) rate, ignoring any attached deal. */
  get standardRate() {
    return this.money(this.raw.stdRate);
  }
  /** The discounted rate, or `undefined` when the unit's `rates` aren't discounted. */
  get discountedRate() {
    return this.raw.rates.discounted ? this.money(this.raw.rates.discountedRate) : void 0;
  }
  /**
   * The rate a customer actually pays — {@link UnitType.discountedRate} where the unit is
   * discounted, otherwise {@link UnitType.rate}.
   *
   * This is *the* price figure: it is what `UnitTypeCollection`'s `sortBy('price')`, `cheapest()`,
   * `priceRange()` and the `maxPrice`/`minPrice` criteria all compare, and what
   * {@link UnitType.formatRate} renders. Defined once here rather than re-derived per call site,
   * because the same expression living in several places is precisely how those paths drifted apart
   * from one another before.
   *
   * Where a deal is attached, the deal decides — via {@link Deal.effectiveRateAgainst} — because a
   * free-period or charge-amount deal carries a `discountedRate` that is **not** an ongoing rate
   * (`0`, or a one-time special price), and quoting it as one is exactly the mistake
   * `getEffectiveDealRate` exists to prevent. Reading `discountedRate` directly would price a
   * "pay $1.10 in your 2nd month" deal at $1.10 a month.
   *
   * With no deal it falls back to `discountedRate ?? rate`, so a unit discounted by its own
   * web/push rate still counts — that discount has no deal to ask.
   */
  get payableRate() {
    const deal = this.deals.promoted();
    return deal ? deal.effectiveRateAgainst(this.rate) : this.discountedRate ?? this.rate;
  }
  /**
   * The unit's physical footprint, in the site's configured size unit.
   *
   * A {@link SiteDimensions}, not a plain `Dimensions`: its `format()` honours the site's
   * `unitDimensionDisplay` setting and will render the **area**, or a swapped order, where the
   * site prefers one. The geometric accessors (`length`, `width`, `area`, `unit`) are the plain
   * ones — `area` is always the computed footprint, whatever the site displays.
   *
   * @example
   * unitType.dimensions.area;     // 15 — always the footprint
   * unitType.dimensions.format(); // "5m × 3m (15m²)", or "15m²" on an area-preferring site
   */
  get dimensions() {
    const { rapidstor, settings } = this.location;
    return new SiteDimensions(this.raw.length, this.raw.width, rapidstor.sizeSymbol, {
      reportedArea: this.raw.area,
      unitDimensionDisplay: settings.unitDimensionDisplay,
      useDescriptorTitle: rapidstor.useDescriptorTitle,
      areaSizeSymbol: rapidstor.areaSizeSymbol
    });
  }
  /**
   * The unit's floor area, as reported by the source (may differ slightly from
   * `dimensions.area`, which is derived from `width × length`).
   */
  get area() {
    return this.raw.area;
  }
  /**
   * Which size bracket this unit type falls in, per the site's configured thresholds.
   *
   * Measured on `dimensions.area` — the **footprint** (`length × width`) — not on
   * {@link UnitType.area}, which is the area the source reports and can differ. The listing has
   * always bracketed on the footprint, and the two figures disagreeing is why
   * `UnitTypeCollection.where({ area })` cannot serve a size filter: it matches on the reported
   * area unless told otherwise.
   */
  get sizeCategory() {
    const settings = this.listingSettings;
    const area = this.dimensions.area;
    if (area <= settings.smallMediumMaxArea()) return "small";
    return area < settings.mediumLargeMaxArea() ? "medium" : "large";
  }
  /**
   * Whether this is a vehicle/parking space rather than storage.
   *
   * Matched on the type name, which is the only signal available — the platform has no notion of a
   * unit *category*, so an operator who names a parking bay something else will not be detected.
   */
  get isVehicleSpace() {
    return this.typeName.toLowerCase().includes("parking");
  }
  /** Whether at least one unit of this type is currently available. */
  get isAvailable() {
    return this.raw.totalAvailable > 0;
  }
  /** Whether this unit type has at least one deal attached. */
  get hasDeal() {
    return (this.raw.deals?.length ?? 0) > 0 || Boolean(this.raw.deal);
  }
  /**
   * The unit type's promoted deal — the one the platform's `unitTypeTransformer` folds into
   * {@link UnitType.rate} — or `undefined` when none is attached.
   *
   * @see UnitType.deals for every deal, which is what a price-card stack needs.
   */
  get deal() {
    return this.raw.deal ? new Deal(this.raw.deal, this.context) : void 0;
  }
  /**
   * Every deal attached to this unit type, wrapped as a query-able collection.
   *
   * {@link UnitType.deal} exposes only the promoted one, which is all a summary line needs; a
   * storefront that renders one selectable price card per deal needs them all.
   *
   * @example
   * unitType.deals.get(selectedDealId)?.effectiveRateAgainst(unitType.rate);
   */
  get deals() {
    return new DealCollection(
      (this.raw.deals ?? []).map((deal) => new Deal(deal, this.context))
    );
  }
  /**
   * The card title for this unit type, e.g. `"5m x 3m Small Locker"`.
   *
   * Composes the two settings that decide it: whether the descriptor's name is preferred over the
   * unit's own type name, and whether the listing variant prefixes a size at all (only the
   * `'standard'` listing does). The size itself is an area or a footprint, per
   * `unitDimensionDisplay`.
   *
   * Falls back to {@link UnitType.typeName} when the site prefers descriptor titles but this unit
   * type has no descriptor — a reservation's snapshot, which was never part of a listing.
   *
   * @returns The display title.
   * @example
   * unitType.title(); // "5m x 3m Small Locker", or "10x10" on a non-standard listing
   */
  title() {
    const display = new Settings(this.context).display();
    const base = display.useDescriptorTitle() ? this.descriptor?.title() ?? this.typeName : this.typeName;
    if (this.listingSettings.variant() !== "standard") return base;
    const size = display.unitDimensionDisplay() === "area" ? this.formatArea() : this.formatDimensions();
    return `${size} ${base}`;
  }
  /**
   * Renders this unit's size the way a RapidStor storefront does — the footprint, or the **area**
   * on a site configured to show one.
   *
   * A preset over {@link UnitType.dimensions}'s own `format()`, translating the storefront's option
   * vocabulary (parens, `W`/`L` labels, no size symbol, unpadded separator) and applying the
   * storefront's defaults: a bare footprint with a ` x ` separator and no trailing area. Together
   * they replace the `descriptorDimensions` helper in `rapidstor-types`, matching its full option
   * matrix.
   *
   * Both routes consult the same setting and share one implementation, so they cannot disagree
   * about *what* to render — only about the punctuation around it.
   *
   * @param options - {@link FormatDimensionsOptions}.
   * @returns e.g. `"5m x 3m"`, `"(5m L/3m W)"`, or `"15m²"` on an area-preferring site.
   * @example
   * unitType.formatDimensions();                                   // "5m x 3m"
   * unitType.formatDimensions({ supplementary: true });             // footprint even on an area site
   * unitType.formatDimensions({ parens: true, labelled: true });    // "(5m L/3m W)"
   * unitType.formatDimensions({ spaceAroundSeparator: false });     // "5mx3m"
   */
  formatDimensions(options = {}) {
    const {
      parens = false,
      labelled = false,
      showSizeSymbol = true,
      spaceAroundSeparator = true,
      supplementary
    } = options;
    const body = this.dimensions.format({
      showArea: false,
      showUnit: showSizeSymbol,
      labelled,
      separator: spaceAroundSeparator ? " x " : "x",
      supplementary
    });
    return parens ? `(${body})` : body;
  }
  /**
   * Renders one of this unit's area figures with the site's area symbol, e.g. `"15m²"`.
   *
   * Which figure is a choice, because a unit type has two and they can disagree: `'reported'` (the
   * default) is {@link UnitType.area}, what the source states and what {@link UnitType.title}
   * prefixes on an area-preferring site; `'footprint'` is `length × width`, what
   * {@link UnitType.sizeCategory} brackets on and what a listing's size cell shows.
   *
   * Public so a caller never has to rebuild this from `utils/formatSize` — doing that by hand means
   * supplying the site's `areaSizeSymbol`/`sizeSymbol` from the settings blob, which is the reach
   * past the domain object this exists to remove. {@link UnitType.formatDimensions} is its
   * `length × width` counterpart.
   *
   * @param options - {@link FormatAreaOptions}.
   * @returns The formatted area, e.g. `"15m²"` or `"15"` without the symbol.
   * @example
   * unitType.formatArea();                                       // "15m²" — the reported figure
   * unitType.formatArea({ basis: 'footprint' });                  // "18m²" — length × width
   * unitType.formatArea({ basis: 'footprint', showSymbol: false }); // "18" — symbol shown separately
   */
  formatArea(options = {}) {
    const { rapidstor } = this.location;
    const area = options.basis === "footprint" ? this.dimensions.area : this.area;
    return formatSize(area, {
      areaSizeSymbol: rapidstor.areaSizeSymbol,
      sizeSymbol: rapidstor.sizeSymbol,
      showSymbol: options.showSymbol ?? true
    });
  }
  /**
   * Formats {@link UnitType.payableRate} — the price a customer actually pays — as a legacy-style
   * price label (matching the storefront's own currency conventions), honouring the site's
   * weekly-rate and billing-default settings unless overridden.
   *
   * Renders the **discounted** figure for a discounted unit, so this cannot contradict the price
   * the collection queries ranked and filtered it by. For the undiscounted figure — a
   * struck-through "was" price, say — use {@link UnitType.formatBaseRate}.
   *
   * @param options - {@link FormatPriceLabelOptions}, merged over the site's own defaults.
   * @returns e.g. `"$249/mo"`.
   * @example
   * unitType.formatRate(); // "$249/mo"
   * unitType.formatRate({ longForm: true }); // "$249 per month"
   */
  formatRate(options = {}) {
    return this.formatAmount(this.payableRate, options);
  }
  /**
   * Formats {@link UnitType.rate} — the rate **before** any discount — in the same currency
   * conventions as {@link UnitType.formatRate}.
   *
   * This is the "was" half of a discounted price: the amount {@link UnitType.payableRate} is
   * discounted *from*, so the two make a coherent pair for a struck-through original beside a
   * current price. When the unit isn't discounted the two are identical, which is exactly why a
   * caller should render this one conditionally (see the example).
   *
   * Not the same as {@link UnitType.standardRate}: that reads the platform's `stdRate` and ignores
   * any attached deal, so it can differ from the figure a deal actually discounted.
   *
   * @param options - {@link FormatPriceLabelOptions}, merged over the site's own defaults.
   * @returns e.g. `"$299/mo"`.
   * @example
   * unitType.formatRate();     // "$249/mo" — what the customer pays
   * unitType.formatBaseRate(); // "$299/mo" — struck through beside it
   *
   * // Only worth showing when there is actually a saving:
   * const was = unitType.payableRate.lessThan(unitType.rate)
   *     ? unitType.formatBaseRate()
   *     : undefined;
   */
  formatBaseRate(options = {}) {
    return this.formatAmount(this.rate, options);
  }
  /**
   * Formats an amount in this site's currency conventions.
   *
   * Shared by {@link UnitType.formatRate} and {@link UnitType.formatBaseRate} so the pair cannot
   * drift in how they render — only in which figure they render.
   *
   * @param amount - The amount to format.
   * @param options - Caller overrides, merged over the site's own defaults.
   * @returns The formatted label.
   */
  formatAmount(amount, options) {
    return formatPriceLabel(amount.major, {
      ...this.priceLabelDefaults(),
      ...options
    });
  }
};

// src/domain/Descriptor.ts
var TAG_ICON_ALIASES = {
  // The old fixed icon picker's id for this icon — not a real FontAwesome name.
  "image-user": "user"
};
function resolveTagIcon(name) {
  return TAG_ICON_ALIASES[name] ?? name;
}
var Descriptor = class extends DomainObject {
  /**
   * @param raw - The friendly `Descriptor` entity this wraps.
   * @param context - The {@link DomainContext} to bind.
   */
  constructor(raw, context) {
    super(context);
    this.raw = raw;
  }
  /** The descriptor's identifier. */
  get id() {
    return this.raw._id;
  }
  /** The descriptor's own name (RapidStor content), regardless of display-title settings. */
  get name() {
    return this.raw.name;
  }
  /** The descriptor's description text. */
  get description() {
    return this.raw.description;
  }
  /** The descriptor's thumbnail/picture image URL. */
  get picture() {
    return this.raw.picture;
  }
  /** Alias of {@link Descriptor.picture}, for callers that think in "thumbnail" terms. */
  get thumbnail() {
    return this.raw.picture;
  }
  /**
   * The descriptor's additional gallery images, beyond {@link Descriptor.picture}.
   *
   * @returns The slide URLs, empty when the operator uploaded none.
   */
  get slides() {
    return this.raw.slides ?? [];
  }
  /** Where this descriptor sorts relative to its siblings. */
  get ordinalPosition() {
    return this.raw.ordinalPosition;
  }
  /** The descriptor's representative floor area, in the site's square size units. */
  get area() {
    return this.raw.area;
  }
  /** Whether this descriptor is enabled for display. */
  get enabled() {
    return this.raw.enabled;
  }
  /** Whether this descriptor is hidden from listings. */
  get hidden() {
    return this.raw.hidden;
  }
  /** The descriptor's descriptive tags, unfiltered (including disabled ones). */
  get tags() {
    return this.raw.tags ?? [];
  }
  /**
   * The descriptor's tags that are actually enabled for display, optionally capped.
   *
   * {@link Descriptor.tags} returns them unfiltered, which almost no caller wants — a storefront
   * repeats `.filter((tag) => tag.enabled).slice(0, 4)` at every render site instead.
   *
   * @param options - `limit` caps how many are returned; omit it for all of them.
   * @returns The enabled tags, in source order.
   * @example
   * descriptor.enabledTags({ limit: 4 });
   */
  enabledTags(options = {}) {
    const enabled = this.tags.filter((tag) => tag.enabled);
    return options.limit === void 0 ? enabled : enabled.slice(0, options.limit);
  }
  /**
   * Resolves a tag's legacy icon id to a real icon name. An instance-bound convenience over the
   * standalone {@link resolveTagIcon} — identical behaviour, callable as `descriptor.resolveTagIcon`.
   *
   * @param name - The tag's raw `icon` id.
   * @returns The resolved icon name.
   * @example
   * descriptor.tags.map((tag) => descriptor.resolveTagIcon(tag.icon));
   */
  resolveTagIcon(name) {
    return resolveTagIcon(name);
  }
  /**
   * This descriptor's unit types, wrapped as a query-able collection.
   *
   * Each unit type is given a back-reference to this descriptor, so it stays reachable once the
   * collection is flattened into a site-wide pool (see {@link UnitType.descriptor}).
   *
   * @example
   * descriptor.unitTypes.available().sortBy('price').first();
   */
  get unitTypes() {
    return new UnitTypeCollection(
      this.raw.unitTypes.map(
        (unitType) => new UnitType(unitType, this.context, { descriptor: this })
      )
    );
  }
  /** Whether at least one of this descriptor's unit types currently has availability. */
  get isAvailable() {
    return this.unitTypes.some((unitType) => unitType.isAvailable);
  }
  /**
   * The descriptor's display title: its own {@link Descriptor.name} when the site is configured
   * to use descriptor titles, otherwise a computed size label (an area, or the first unit type's
   * dimensions) — mirroring the legacy storefront's `descriptorTitle` precedence.
   *
   * @returns The display title.
   * @example
   * descriptor.title(); // "Small Locker" or "9m²", depending on site settings
   */
  title() {
    const { rapidstor, settings } = this.location;
    if (rapidstor.useDescriptorTitle) {
      return this.translatedName();
    }
    if (settings.unitDimensionDisplay === "area") {
      return this.sizeLabel();
    }
    const [firstUnitType] = this.raw.unitTypes;
    if (firstUnitType) {
      return new Dimensions(
        firstUnitType.length,
        firstUnitType.width,
        rapidstor.sizeSymbol
      ).format({
        showArea: false,
        order: settings.unitDimensionDisplay === "widthByLength" ? "widthFirst" : "lengthFirst"
      });
    }
    return this.translatedName();
  }
  /**
   * The descriptor's name, resolved through the context's translator.
   *
   * Descriptor names are operator content, and a corporation may rename one across its whole
   * estate — so the name is a translatable string, not a fixed value. The key stays closed
   * (`descriptor.name`) with the name itself passed as a param, so a resolver can look up a rename
   * by that value without the key set growing per descriptor.
   *
   * @returns The display name.
   */
  translatedName() {
    return this.text("descriptor.name", this.raw.name, { name: this.raw.name });
  }
  /**
   * The descriptor's floor area, formatted for display — the same figure {@link Descriptor.title}
   * shows when the site prefers area over dimensions.
   *
   * @returns e.g. `"9m²"`.
   */
  sizeLabel() {
    const { rapidstor } = this.location;
    return formatSize(this.raw.area, {
      areaSizeSymbol: rapidstor.areaSizeSymbol,
      sizeSymbol: rapidstor.sizeSymbol
    });
  }
  /**
   * A price-forward summary of this descriptor, suitable for a listing card.
   *
   * @returns The {@link DescriptorSummary}.
   * @example
   * const { title, priceFrom, thumbnail } = descriptor.summary();
   */
  summary() {
    return {
      title: this.title(),
      priceFrom: this.priceRange()?.from,
      sizeLabel: this.sizeLabel(),
      thumbnail: this.thumbnail,
      tags: this.tags
    };
  }
  /**
   * The rate span across this descriptor's unit types, as a customer would see it.
   *
   * Delegates to {@link UnitTypeCollection.priceRange} rather than reducing over
   * `unitType.rate`, so a discounted unit contributes what it actually costs. Reducing over the
   * list rate made this disagree with `unitTypes.cheapest()` and `sortBy('price')` — a descriptor
   * could advertise "from $249" while its cheapest unit sold for $199.
   *
   * @returns The {@link DescriptorPriceRange}, or `undefined` when the descriptor has no unit
   *   types to price. `undefined` rather than a zero range, both to match
   *   `UnitTypeCollection.priceRange` and because a `0` bound is indistinguishable from a
   *   genuinely free unit.
   * @example
   * const range = descriptor.priceRange();
   * range?.from.format();
   */
  priceRange() {
    return this.unitTypes.priceRange();
  }
};

// ../rapidstor-types/dist/react/entities/UnitType.mjs
var ratesTransformer = (rates) => {
  return {
    discounted: rates.discounted,
    // Always set discountedRate, even if there's no discount.
    // It makes price comparison between units much simpler.
    discountedRate: rates.discounted ? rates.discountedRate : rates.rate,
    rate: rates.rate,
    taxInclusive: rates.taxInclusive
  };
};
var unitTypeTransformer = (unitType, location) => {
  let deal = void 0;
  let rates;
  if (unitType.deals.length > 0) {
    const [firstDeal] = unitType.deals;
    deal = {
      _id: firstDeal._id,
      title: firstDeal.title,
      descriptions: firstDeal.descriptions,
      tags: firstDeal.tags,
      deleted: firstDeal.deleted,
      customFormat: firstDeal.customFormat,
      discountPlan: firstDeal.discountPlan,
      enable: firstDeal.enable,
      public: firstDeal.public,
      rates: firstDeal.rates,
      sLocationCode: firstDeal.sLocationCode,
      personas: firstDeal.personas
    };
    if (firstDeal.discountPlan) {
      deal.discountPlan = {
        _id: firstDeal.discountPlan._id,
        ConcessionID: firstDeal.discountPlan.ConcessionID,
        dcRoundTo: firstDeal.discountPlan.dcRoundTo,
        bRound: firstDeal.discountPlan.bRound,
        dcPCDiscount: firstDeal.discountPlan.dcPCDiscount,
        dcFixedDiscount: firstDeal.discountPlan.dcFixedDiscount,
        dcChgAmt: firstDeal.discountPlan.dcChgAmt,
        iAmtType: firstDeal.discountPlan.iAmtType,
        iPrePaidMonths: firstDeal.discountPlan.iPrePaidMonths,
        bPrepay: firstDeal.discountPlan.bPrepay,
        iExpirMonths: firstDeal.discountPlan.iExpirMonths,
        bNeverExpires: firstDeal.discountPlan.bNeverExpires,
        sPlanName: firstDeal.discountPlan.sPlanName,
        iInMonth: firstDeal.discountPlan.iInMonth
      };
    }
    rates = ratesTransformer(unitType.deals[0].rates);
  } else {
    rates = ratesTransformer(unitType.rates);
  }
  let totalAvailable = unitType.iTotalVacant;
  if (location?.rapidstor.includeReservedUnitsInOccupied) {
    totalAvailable -= unitType.iTotalReserved;
  }
  return {
    corpCode: unitType.sCorpCode,
    locationCode: unitType.sLocationCode,
    unitTypeId: unitType.UnitTypeID,
    typeName: unitType.sTypeName,
    deleted: unitType.deleted,
    stdRate: unitType.dcStdRate,
    stdWeeklyRate: unitType.dcStdWeeklyRate,
    area: unitType.dcArea,
    width: unitType.dcWidth,
    length: unitType.dcLength,
    floor: unitType.iFloor,
    inside: unitType.bInside,
    hasComputedAvailable: unitType.hasComputedAvailable,
    totalOccupied: unitType.iTotalOccupied,
    totalReserved: unitType.iTotalReserved,
    totalUnits: unitType.iTotalUnits,
    totalVacant: unitType.iTotalVacant,
    totalAvailable,
    firstAvailableUnitName: unitType.sUnitName_FirstAvailable,
    representativeUnitName: unitType.sUnitName_Representative,
    reservationFee: unitType.dcReservationFee,
    excludeFromInsurance: unitType.bExcludeFromInsurance,
    units: unitType.units,
    _id: unitType._id,
    deal,
    deals: unitType.deals,
    rates,
    unitToUse: unitType.unitToUse
  };
};

// src/domain/hydrate.ts
function toFriendlyDescriptor(raw, location) {
  const unitTypes = raw.unitTypes.map((unitType) => unitTypeTransformer(unitType, location));
  return {
    _id: raw._id,
    locationCode: raw.sLocationCode,
    corpCode: raw.sCorpCode,
    name: raw.name,
    description: raw.description,
    descriptions: raw.descriptions,
    tags: raw.tags,
    picture: raw.picture,
    slides: raw.slides,
    defaultInsuranceCoverage: raw.defaultInsuranceCoverage,
    deals: raw.deals,
    enabled: raw.enabled,
    hidden: raw.hidden,
    unitTypes,
    ordinalPosition: raw.ordinalPosition,
    // `descriptorMinArea` is private to rapidstor-types, so derive the same value here: the
    // smallest area across the descriptor's unit types, and 0 when it has none.
    area: unitTypes.length > 0 ? Math.min(...unitTypes.map((unit) => unit.area)) : 0,
    specialText: raw.specialText,
    jblOptionText: raw.jblOptionText,
    upgradesTo: raw.upgradesTo,
    useForCarousel: raw.useForCarousel
  };
}

// src/domain/Site.ts
var Site = class _Site extends DomainObject {
  /**
   * @param raw - The friendly `Location` entity this site wraps.
   * @param context - The {@link DomainContext} to bind — ordinarily `{ location: raw, codes }`,
   *   since a `Site` and the location its own context binds are the same entity.
   */
  constructor(raw, context) {
    super(context);
    this.raw = raw;
  }
  /**
   * Builds a `Site` from the raw SiteLink-shaped API payload, running the `rapidstor-types`
   * `locationTransformer` to produce the friendly `Location` this class wraps, and binding a
   * self-consistent {@link DomainContext} in one step.
   *
   * @param raw - The raw `types/Site` payload, as returned by the API.
   * @param codes - The corp/site codes this site is scoped to.
   * @param options - {@link SiteOptions} — the context parts not derived from `raw`.
   * @returns A new `Site`.
   * @example
   * const site = Site.from(apiResponse, {
   *     corpCode: corpCode('ACME'),
   *     locationCode: locationCode('ACME-01'),
   * });
   */
  static from(raw, codes, options = {}) {
    return _Site.fromLocation(locationTransformer(raw), codes, options);
  }
  /**
   * Builds a `Site` from a location that has **already** been transformed into its friendly entity
   * form, binding a self-consistent {@link DomainContext}.
   *
   * {@link Site.from} runs `locationTransformer` itself, which assumes the SDK made the fetch. A
   * consumer whose data layer transforms at its own API boundary already holds a
   * `react/entities/Location` — the very thing `Site.from` produces — and previously had to
   * assemble the context by hand and rely on the undocumented fact that a `Site`'s context is
   * self-referential.
   *
   * @param location - The friendly `Location` entity.
   * @param codes - The corp/site codes this site is scoped to.
   * @param options - {@link SiteOptions}.
   * @returns A new `Site`.
   * @example
   * const site = Site.fromLocation(locationFromMyStore, client.codes, { translate });
   */
  static fromLocation(location, codes, options = {}) {
    return new _Site(location, { location, codes, translate: options.translate });
  }
  /** The site's display name. */
  get name() {
    return this.raw.name;
  }
  /** The URL-friendly slug derived from {@link Site.name}. */
  get slug() {
    return this.raw.slug;
  }
  /** The validated site/location code. */
  get locationCode() {
    return locationCode(this.raw.locationCode);
  }
  /** The validated corporation code. */
  get corpCode() {
    return corpCode(this.raw.corpCode);
  }
  /** The site's latitude. */
  get latitude() {
    return this.raw.latitude;
  }
  /** The site's longitude. */
  get longitude() {
    return this.raw.longitude;
  }
  /** The site's UTC offset, in the source's native unit, passed through as-is. */
  get timezoneOffset() {
    return this.raw.timezoneOffset;
  }
  /** The site's postal address, as a value object. */
  get address() {
    return Address.from(this.raw.address);
  }
  /**
   * Formats the site's address as a single line.
   *
   * @param options - {@link AddressFormatOptions}.
   * @returns e.g. `"12 Smith St, Fitzroy, VIC, 3065, Australia"`.
   * @example
   * site.formatAddress({ includeCountry: false });
   */
  formatAddress(options) {
    return this.address.format(options);
  }
  /** The site's operating hours (per-day office open/close times). */
  get hours() {
    return this.raw.hours;
  }
  /**
   * The site's typed settings facade — grouped, intent-named accessors over the raw `rapidstor`
   * settings blob, bound to this same context.
   */
  get settings() {
    return new Settings(this.context);
  }
  /**
   * Formats any amount in this site's own currency conventions, e.g. `"$249/mo"`.
   *
   * `UnitType.formatRate` and `Deal.priceDisplay` cover the amounts those objects own, but a
   * storefront also renders figures that belong to neither — a deal's rate measured against a
   * *unit's* rate, a computed saving, a fee. Without this, a caller has to rebuild the site's
   * symbol / weekly-rate / billing-default options by hand from the settings blob, which is what
   * this facade exists to avoid.
   *
   * Reproduces the legacy storefront's conventions rather than `Money.format`'s `Intl` output —
   * see [`docs/utils.md`](../utils.md#currency) for the distinction.
   *
   * @param amount - The amount, as {@link Money} or raw major units.
   * @param options - {@link FormatPriceLabelOptions}, merged over the site's own defaults.
   * @returns e.g. `"$249/mo"`, or `"$249"` with `shortForm`.
   * @example
   * site.formatRate(deal.effectiveRateAgainst(unitType.rate), { shortForm: true }); // "$199"
   * site.periodSuffix();                                                            // "/mo"
   */
  formatRate(amount, options = {}) {
    return formatPriceLabel(amount instanceof Money ? amount.major : amount, {
      ...this.priceLabelDefaults(),
      ...options
    });
  }
  /**
   * The billing-period suffix on its own, e.g. `"/mo"` or `"/wk"`.
   *
   * A storefront that renders the amount and its period in separate elements needs the suffix
   * without an amount attached. Derived by diffing the labelled and bare forms of the same figure,
   * so it cannot drift from what {@link Site.formatRate} actually appends.
   *
   * @param options - {@link FormatPriceLabelOptions}; `longForm` selects `" per month"`.
   * @returns The suffix, empty when `shortForm` is set.
   * @example
   * site.periodSuffix();                    // "/mo"
   * site.periodSuffix({ longForm: true });  // " per month"
   */
  periodSuffix(options = {}) {
    const labelled = this.formatRate(0, options);
    const bare = this.formatRate(0, { ...options, shortForm: true });
    return labelled.slice(bare.length);
  }
  /**
   * Hydrates a raw unit-list payload into query-able collections bound to this site.
   *
   * The API returns the un-transformed backend shape; this converts it to the friendly entities
   * the domain wrappers expect and binds them to this site's context, which is what lets the
   * results format money and sizes without a `location` argument.
   *
   * Nothing is filtered out during hydration — use `descriptors.available()` or
   * `unitTypes.where({ available: true })` to narrow, so the filtering is visible at the call site.
   *
   * @param raw - The payload from `client.units.listByType(...)`.
   * @returns The hydrated {@link SiteListing}.
   * @example
   * const listing = site.hydrate(await client.units.listByType({ sites: [site.locationCode] }));
   * listing.descriptors.get(descriptorId)?.summary();
   * listing.unitTypes.where({ available: true }).sortBy('price').first();
   */
  hydrate(raw) {
    return this.hydrateDescriptors(
      raw.descriptors.map((descriptor) => toFriendlyDescriptor(descriptor, this.raw))
    );
  }
  /**
   * Binds descriptors that have **already** been transformed into their friendly entity form,
   * returning the same query-able collections {@link Site.hydrate} does.
   *
   * `hydrate` owns the transform, which assumes the SDK made the fetch. A consumer with its own
   * data layer — a cache, a store, an app that transforms at its API boundary — holds
   * `react/entities/Descriptor` objects already and has nowhere to hand them. This is that entry
   * point, and `hydrate` now delegates to it so there is one code path.
   *
   * Worth knowing why this is not merely a convenience: a consumer's own transform step may apply
   * product logic the SDK has no equivalent for — `apps/rapidstor-frontend`, for instance, does
   * upgrade substitution across descriptors and drops hidden/disabled ones before a screen ever
   * sees them. Re-running `hydrate` on the raw payload would discard that.
   *
   * @param descriptors - Friendly descriptor entities.
   * @returns The hydrated {@link SiteListing}.
   * @example
   * const { descriptors, unitTypes } = site.hydrateDescriptors(alreadyTransformedList);
   */
  hydrateDescriptors(descriptors) {
    const wrapped = descriptors.map(
      (descriptor) => new Descriptor(descriptor, this.context)
    );
    return {
      descriptors: new DescriptorCollection(wrapped),
      // Descriptor.unitTypes is itself a collection; flatten to one site-wide view.
      unitTypes: new UnitTypeCollection(
        wrapped.flatMap((descriptor) => descriptor.unitTypes.toArray())
      )
    };
  }
  /**
   * Builds a site from its raw payload and hydrates its listing in one step.
   *
   * @param rawSite - The raw site payload from `client.locations.getByCode(...)`.
   * @param rawListing - The payload from `client.units.listByType(...)`.
   * @param codes - The corp/site codes this site is scoped to.
   * @param options - {@link SiteOptions} — the context parts not derived from the payloads.
   * @returns The {@link Site} plus its hydrated collections.
   * @example
   * const { site, descriptors } = Site.fromListing(rawSite, rawListing, codes);
   */
  static fromListing(rawSite, rawListing, codes, options = {}) {
    const site = _Site.from(rawSite, codes, options);
    return { site, ...site.hydrate(rawListing) };
  }
};

// src/domain/Reservation.ts
function adaptReservationUnitType(raw) {
  return {
    corpCode: raw.corpCode,
    locationCode: raw.locationCode,
    unitTypeId: raw.typeId,
    typeName: raw.type,
    deleted: false,
    stdRate: raw.standardRate,
    stdWeeklyRate: raw.weeklyRate,
    area: raw.area,
    width: raw.width,
    length: raw.length,
    floor: raw.floor,
    inside: raw.inside,
    hasComputedAvailable: false,
    totalOccupied: 0,
    totalReserved: 0,
    totalUnits: 0,
    totalVacant: 0,
    totalAvailable: 0,
    firstAvailableUnitName: raw.firstAvailableUnitName,
    representativeUnitName: raw.representativeUnitName,
    reservationFee: 0,
    excludeFromInsurance: raw.excludeFromInsurance,
    units: [],
    _id: raw._id,
    deal: void 0,
    deals: [],
    rates: {
      discounted: raw.preferredRate !== raw.standardRate,
      discountedRate: raw.preferredRate,
      rate: raw.standardRate,
      taxInclusive: raw.chargeTax1 || raw.chargeTax2
    },
    unitToUse: raw.unitToUse
  };
}
var Reservation = class extends DomainObject {
  /**
   * @param raw - The raw `BaseReservation` this wraps.
   * @param context - The {@link DomainContext} to bind.
   */
  constructor(raw, context) {
    super(context);
    this.raw = raw;
  }
  /** The reservation's identifier. */
  get id() {
    return this.raw._id;
  }
  /** The reservation's validated public token. */
  get token() {
    return reservationToken(this.raw.token);
  }
  /** Whether the reservation has completed move-in (finalized). */
  get isFinalized() {
    return this.raw.finalized === true;
  }
  /**
   * The reservation's primary contact details.
   *
   * @returns The {@link ReservationContact}.
   * @example
   * reservation.contact().email;
   */
  contact() {
    return {
      firstName: this.raw.firstName,
      lastName: this.raw.lastName,
      email: this.raw.email,
      phone: this.raw.phone,
      company: this.raw.company
    };
  }
  /**
   * The reservation's tenant-account details, from its move-in context.
   *
   * @returns The {@link ReservationTenantInfo}.
   * @example
   * reservation.tenant().commercial;
   */
  tenant() {
    const { tenant } = this.raw.moveInContext;
    return {
      id: tenant.tenantId,
      commercial: tenant.commercial,
      autoBill: tenant.autoBill,
      preExisting: tenant.preExisting
    };
  }
  /** The reservation's postal address (country isn't carried on a reservation, so it is blank). */
  get address() {
    return new Address(
      this.raw.addr1 ?? "",
      this.raw.addr2 ?? "",
      this.raw.city ?? "",
      this.raw.region ?? "",
      this.raw.postalCode ?? "",
      ""
    );
  }
  /**
   * Formats the reservation's address as a single line. Defaults to omitting the country, since a
   * reservation doesn't carry one (see {@link Reservation.address}).
   *
   * @param options - {@link AddressFormatOptions}.
   * @returns e.g. `"12 Smith St, Fitzroy, VIC, 3065"`.
   */
  formatAddress(options) {
    return this.address.format({ includeCountry: false, ...options });
  }
  /**
   * The unit type this reservation is for, wrapped, or `undefined` when the reservation carries
   * no unit-type snapshot. See {@link adaptReservationUnitType} for the shape caveats.
   */
  get unitType() {
    return this.raw.unitType ? new UnitType(adaptReservationUnitType(this.raw.unitType), this.context) : void 0;
  }
  /**
   * The deal attached to this reservation, wrapped, or `undefined` when none is attached. Checked
   * defensively (`deal._id` truthy) rather than trusting the type's non-optional `deal` field,
   * since a "no deal" reservation is represented as an empty/absent object at runtime.
   */
  get deal() {
    return this.raw.deal && this.raw.deal._id ? new Deal(this.raw.deal, this.context) : void 0;
  }
  /** The reservation's standard (non-discounted) rate. */
  get rate() {
    return this.money(this.raw.rates.rate);
  }
  /** The reservation's discounted rate, or `undefined` when its rates aren't discounted. */
  get discountedRate() {
    return this.raw.rates.discounted ? this.money(this.raw.rates.discountedRate ?? this.raw.rates.rate) : void 0;
  }
  /** The quoted total for this reservation. */
  get total() {
    return this.money(this.raw.quotedRate);
  }
};

export { AutoBillType, Collection, Deal, DealCollection, Descriptor, DescriptorCollection, DomainObject, Reservation, Settings, Site, SiteDimensions, UnitType, UnitTypeCollection, corpCode, domain_exports, locationCode, reservationToken, resolveTagIcon, toFriendlyDescriptor, unitTypeId };
//# sourceMappingURL=chunk-VPMR5HY6.js.map
//# sourceMappingURL=chunk-VPMR5HY6.js.map